import { useCallback, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { getWelcomeSheet, sendResumeLink } from '@/services/intake.service.js';
import { toastSmsWarnings } from '@/lib/smsWarnings.js';
import SmsPermissionControl from '@/components/intake/SmsPermissionControl.jsx';

// MV-01 3.4 (Ship 1b): the broker UI for the 1a send-resume-link endpoint.
// Text or email the tokenized /apply link that reopens the SAME draft; the
// server logs the send and creates the follow-up task. The text permission
// store's answer is visible for the text channel; the permission gate decides
// a send without it (people-create-door T7c: 409/503 enforce, a warning toast in
// shadow). The no-consent confirm checkbox is gone, the gate owns the fact;
// David records or stops permission by hand through SmsPermissionControl (T7e).
export default function ResumeLinkSheet({ dealId, open, onOpenChange, onChange }) {
  const [sheet, setSheet] = useState(null);
  const [channel, setChannel] = useState('sms');
  const [sending, setSending] = useState(false);

  // The welcome sheet carries the contact/phone/permission context this sheet
  // needs; the resume-link endpoint itself resolves the session server-side.
  // A failed re-read keeps the sheet already shown (the phone stays) and says so.
  const load = useCallback(() => getWelcomeSheet(dealId)
    .then((d) => setSheet(d.sheet))
    .catch((err) => {
      setSheet((prev) => prev || {});
      toast.error(`Could not read the text permission again: ${(err instanceof ApiError && err.body?.error) || err.message || 'unknown error'}`);
    }), [dealId]);

  useEffect(() => {
    if (!open || !dealId) return;
    setSheet(null);
    load();
  }, [open, dealId, load]);

  const optedOut = sheet?.opted_out === true;
  const smsBlocked = channel === 'sms' && (optedOut || !sheet?.phone);
  const canSend = !!sheet && !sending && !smsBlocked;

  async function handleSend() {
    setSending(true);
    try {
      const res = await sendResumeLink(dealId, { channel });
      toast.success(`Resume link sent by ${channel === 'sms' ? 'text' : 'email'}`);
      toastSmsWarnings(res?.warnings);
      if (res?.url) {
        try { await navigator.clipboard.writeText(res.url); toast(`Link copied: ${res.url}`); } catch { /* clipboard optional */ }
      }
      onChange?.();
      onOpenChange?.(false);
    } catch (err) {
      const code = err instanceof ApiError ? err.body?.code : null;
      if (code === 'SMS_OPTED_OUT') toast.error('This number has opted out of texts.');
      else if (code === 'SMS_NO_PERMISSION' || code === 'SMS_PERMISSION_UNREADABLE') toast.error(err.body?.error || 'Text not sent: no text permission.');
      else if (code === 'ALREADY_SUBMITTED') toast.error('This intake was already submitted; nothing to resume.');
      else toast.error(`Send failed: ${err.message || 'unknown error'}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Send resume link</SheetTitle>
          <SheetDescription>
            Reopens the client's saved application draft exactly where it left off. A follow-up task lands on the deal.
          </SheetDescription>
        </SheetHeader>

        {!sheet ? (
          <p className="mt-6 text-sm text-text-muted">Loading…</p>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="flex gap-2">
              {['sms', 'email'].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setChannel(c)}
                  className={`flex-1 rounded border px-3 py-2 text-sm ${channel === c ? 'border-accent bg-accent/10 text-text' : 'border-border text-text-muted hover:text-text'}`}
                >
                  {c === 'sms' ? 'Text' : 'Email'}
                </button>
              ))}
            </div>

            <div className="rounded border border-border bg-surface px-3 py-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-text">{sheet.contact_name || 'Client'}</span>
                <span className="text-text-muted">{channel === 'sms' ? (sheet.phone || 'No phone on intake') : 'Sends to the intake email'}</span>
              </div>
              {channel === 'sms' && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <SmsPermissionControl phone={sheet.phone} state={sheet.permission} at={sheet.permission_at} crmContactId={sheet.crm_contact_id} onChanged={load} />
                </div>
              )}
            </div>

            <Button onClick={handleSend} disabled={!canSend} className="w-full">
              {sending ? 'Sending…' : `Send resume link by ${channel === 'sms' ? 'text' : 'email'}`}
            </Button>
            <p className="text-[11px] text-text-dim">Logged on the deal timeline; an unfinished-intake follow-up task is created automatically.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
